import type { components } from "@repo/api-types/merchant"
import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import type {
  HandoverBizData,
  HandoverProductStatisticsRequest,
  HandoverProductStatisticsResponse,
  ShiftHandoverConfirmRequest,
  ShiftHandoverOpenNextRequest,
  ShiftHandoverPreviewRequest,
  ShiftHandoverSnapshot,
} from "./types/handover"
import type * as Worker from "./types/worker"
import type { CallerHandledAuthFailureOptions } from "@/utils/callerHandledAuthFailure"
import { cloneDeep } from "lodash-es"
import { getCallerHandledAuthFailureConfig } from "@/utils/callerHandledAuthFailure"
import { AmountConverter, DiscountRateConverter } from "@/utils/discountRate"
import { request } from "@/utils/service"
import { isNilOrEmptyStr } from '@/utils/validate'
import { getHandoverErrorMessage, HandoverRejectedError, isHandoverBusinessFailure, toHandoverRequestError } from "./handoverFailure"

const PREFIX = "teashop_merchant/"

export type HandoverBizDataSection =
  | "funds"
  | "income"
  | "stock"
  | "bill"
  | "table"
  | "member"
  | "commission"
  | "operation"

export interface HandoverSectionBizDataResponse {
  merchantShiftHandoverRecordId?: number
  handoverStartTime?: string | null
  handoverEndTime?: string | null
  section?: HandoverBizDataSection
  bizData?: HandoverBizData
}

export type HandoverEndTime = string | Date | null

/** 获取角色列表 */
export function getRoleListByShopId(shopId: number) {
  return request<Worker.RoleListData>({
    url: `${PREFIX}merchant_role/list/${shopId}`,
    method: "GET",
  })
}

/** 获取角色列表包含权限信息 */
export function getRoleDetailListByShopId(
  shopId: number,
  options: CallerHandledAuthFailureOptions & {
    businessContextSystemVersion?: SystemVersionDTO
  } = {},
) {
  return request<Worker.RoleDetailListData>({
    url: `${PREFIX}merchant_role/detail_list/${shopId}`,
    method: "GET",
    businessContextSystemVersion: options.businessContextSystemVersion,
    ...getCallerHandledAuthFailureConfig(options),
    transformResponse: [(rawData) => {
      if (typeof rawData === "string") {
        const parsedData = JSON.parse(rawData) as Worker.RoleDetailListData
        const transformedData = parsedData.data.map((data) => {
          const transformData = cloneDeep(data)
          if (!isNilOrEmptyStr(data.businessPermissionDetail?.itemDiscountLimit)) {
            transformData.businessPermissionDetail.itemDiscountLimit = DiscountRateConverter.toFrontendRawValue(data.businessPermissionDetail.itemDiscountLimit)
          }
          if (!isNilOrEmptyStr(data.businessPermissionDetail?.itemWaiverLimit)) {
            transformData.businessPermissionDetail.itemWaiverLimit = AmountConverter.toFrontendRawValue(data.businessPermissionDetail.itemWaiverLimit)
          }
          if (!isNilOrEmptyStr(data.businessPermissionDetail?.orderDiscountLimit)) {
            transformData.businessPermissionDetail.orderDiscountLimit = DiscountRateConverter.toFrontendRawValue(data.businessPermissionDetail.orderDiscountLimit)
          }

          if (!isNilOrEmptyStr(data.businessPermissionDetail?.orderWaiverLimit)) {
            transformData.businessPermissionDetail.orderWaiverLimit = AmountConverter.toFrontendRawValue(data.businessPermissionDetail.orderWaiverLimit)
          }
          if (!isNilOrEmptyStr(data.businessPermissionDetail?.manualZeroLimit)) {
            transformData.businessPermissionDetail.manualZeroLimit = AmountConverter.toFrontendRawValue(data.businessPermissionDetail.manualZeroLimit)
          }
          if (!isNilOrEmptyStr(data.businessPermissionDetail?.freeOrderLimit)) {
            transformData.businessPermissionDetail.freeOrderLimit = AmountConverter.toFrontendRawValue(data.businessPermissionDetail.freeOrderLimit)
          }
          return transformData
        })
        return {
          ...parsedData,
          data: transformedData,
        }
      } else {
        return rawData
      }
    }],
  })
}

/** 创建角色 */
export function createRole(data: Worker.RoleCreateType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_role/add_role_with_permission`,
    method: "POST",
    data,
    transformRequest: [(data: Worker.RoleCreateType) => {
      const transformData = cloneDeep(data)
      if (!isNilOrEmptyStr(data.businessPermissionDetail.itemDiscountLimit)) {
        transformData.businessPermissionDetail.itemDiscountLimit = DiscountRateConverter.toBackendRawValue(data.businessPermissionDetail.itemDiscountLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.itemWaiverLimit)) {
        transformData.businessPermissionDetail.itemWaiverLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.itemWaiverLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.orderDiscountLimit)) {
        transformData.businessPermissionDetail.orderDiscountLimit = DiscountRateConverter.toBackendRawValue(data.businessPermissionDetail.orderDiscountLimit)
      }

      if (!isNilOrEmptyStr(data.businessPermissionDetail.orderWaiverLimit)) {
        transformData.businessPermissionDetail.orderWaiverLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.orderWaiverLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.manualZeroLimit)) {
        transformData.businessPermissionDetail.manualZeroLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.manualZeroLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.freeOrderLimit)) {
        transformData.businessPermissionDetail.freeOrderLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.freeOrderLimit)
      }
      return JSON.stringify(transformData)
    }],
  })
}

/** 获取角色权限信息 */
export function getRolePermissionListByRoleId(roleId: number) {
  return request<Worker.RoleCreateType>({
    url: `${PREFIX}merchant_role/get_role_with_permission/${roleId}`,
    method: "GET",
  })
}

/** 修改角色权限 */
export function updateRolePermission(req: Worker.RoleCreateType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_role/update_role_with_permission`,
    method: "POST",
    data: req,
    transformRequest: [(data: Worker.RoleCreateType) => {
      const transformData = cloneDeep(data)
      if (!isNilOrEmptyStr(data.businessPermissionDetail.itemDiscountLimit)) {
        transformData.businessPermissionDetail.itemDiscountLimit = DiscountRateConverter.toBackendRawValue(data.businessPermissionDetail.itemDiscountLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.itemWaiverLimit)) {
        transformData.businessPermissionDetail.itemWaiverLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.itemWaiverLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.orderDiscountLimit)) {
        transformData.businessPermissionDetail.orderDiscountLimit = DiscountRateConverter.toBackendRawValue(data.businessPermissionDetail.orderDiscountLimit)
      }

      if (!isNilOrEmptyStr(data.businessPermissionDetail.orderWaiverLimit)) {
        transformData.businessPermissionDetail.orderWaiverLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.orderWaiverLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.manualZeroLimit)) {
        transformData.businessPermissionDetail.manualZeroLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.manualZeroLimit)
      }
      if (!isNilOrEmptyStr(data.businessPermissionDetail.freeOrderLimit)) {
        transformData.businessPermissionDetail.freeOrderLimit = AmountConverter.toBackendRawValue(data.businessPermissionDetail.freeOrderLimit)
      }
      return JSON.stringify(transformData)
    }],
  })
}

/** 获取权限列表 */
export function getPermissionListByShopId() {
  return request<Worker.PermissionListData>({
    url: `${PREFIX}merchant_permission/list`,
    method: "GET",
  })
}

/**
 * 获取所有权限列表
 */
export function getPermissionListAll(options: CallerHandledAuthFailureOptions & {
  businessContextSystemVersion?: SystemVersionDTO
} = {}) {
  return request<Worker.PermissionListData>({
    url: `${PREFIX}merchant_permission/list_all`,
    method: "GET",
    businessContextSystemVersion: options.businessContextSystemVersion,
    ...getCallerHandledAuthFailureConfig(options),
  })
}

/** 获取员工列表 */
export function getWorkerListByShopId(shopId: number, permissionList: string[]) {
  return request<Worker.WorkerListData>({
    url: `${PREFIX}merchant_user/list_by_shop_id/${shopId}`,
    method: "POST",
    data: permissionList,
  })
}

/** 创建员工 */
export function createWorker(data: Worker.WorkerCreateType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_user/save_with_role`,
    method: "POST",
    data,
  })
}

/** 获取员工信息 */
export function getWorkerInfoByUserId(userId: number) {
  return request<IApiResponseData<Worker.WorkerCreateType>>({
    url: `${PREFIX}merchant_user/user_id/${userId}/info`,
    method: "GET",
  })
}

/** 修改员工信息 */
export function updateWorker(data: Worker.WorkerCreateType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_user/update_with_role`,
    method: "POST",
    data,
  })
}

/** 停用启用员工(修改员工状态) */
export function editWorkerStatus(data: Worker.MerchantUserEditVO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_user/edit_status`,
    method: "PUT",
    data,
  })
}

/** 通过角色id查询相关联的用户列表 */
export function getUserListByRoleId(roleId: number) {
  return request<IApiResponseData<Worker.WorkerType[]>>({
    url: `${PREFIX}merchant_user/list_by_role/${roleId}`,
    method: "GET",
  })
}

/** 删除角色与员工关联关系 */
export function deleteRoleUserRelation(roleId: number, userId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_role/delete_role_user_relation/${roleId}/${userId}`,
    method: "DELETE",
  })
}

export interface DeleteRoleOptions {
  rebindUsersToSourceTemplate?: boolean
}

/** 删除角色 */
export function deleteRole(roleId: number, options: DeleteRoleOptions = {}) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_role/${roleId}`,
    method: "DELETE",
    params: {
      rebindUsersToSourceTemplate: options.rebindUsersToSourceTemplate ?? false,
    },
  })
}

/** 删除员工 */
export function deleteWorker(userId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_user/${userId}`,
    method: "DELETE",
  })
}

/** 获取员工列表 */
export function getWorkerDetailListByShopId(shopId: number) {
  return request<Worker.WorkerDetailListData>({
    url: `${PREFIX}merchant_user/detail_list_by_shop_id/${shopId}`,
    method: "GET",
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 获取当前有效提成方案涉及的提成员工并集 */
export function getEffectiveCommissionEmployeeList() {
  return request<Worker.CommissionEmployeeListData>({
    url: `${PREFIX}merchant/commission/rule/effective-employees`,
    method: "GET",
  })
}

/** 验证手机号 */
export function verifyPhone(phone: string) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_user/verify_phone/${phone}`,
    method: "GET",
  })
}

/** 修改用户姓名 */
export function updateName(data: Worker.WorkerType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_user/update_name`,
    method: "PUT",
    data,
  })
}

/** 开班 */
export function open(data: Worker.HandoverType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shift_handover_record/open`,
    method: "POST",
    data,
  })
}

/** 交班 */
export function handover(data: Worker.HandoverType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shift_handover_record/handover`,
    method: "POST",
    data,
  })
}

/** Preview authoritative handover statistics for one effective cutoff. */
export function previewShiftHandover(data: ShiftHandoverPreviewRequest) {
  return request<IApiResponseData<ShiftHandoverSnapshot>>({
    url: `${PREFIX}merchant_shift_handover_record/preview`,
    method: "POST",
    data,
  })
}

/** Confirm handover against the version returned by preview. */
export async function confirmShiftHandover(data: ShiftHandoverConfirmRequest) {
  try {
    const response = await request<IApiResponseData<ShiftHandoverSnapshot>>({
      url: `${PREFIX}merchant_shift_handover_record/confirm`,
      method: "POST",
      data,
      skipBusinessCodeCheck: true,
      skipGlobalErrorToast: true,
    })
    if (isHandoverBusinessFailure(response)) {
      throw new HandoverRejectedError(getHandoverErrorMessage(response, "交班未完成，请更新统计后重新核对"))
    }
    if (response?.code !== 0 || response.data?.confirmed !== true || Number(response.data.recordId) !== data.recordId) {
      throw new Error("服务端未确认交班结果")
    }
    return response
  } catch (error) {
    throw toHandoverRequestError(error)
  }
}

/** Read the saved immutable snapshot (or the explicit legacy fallback). */
export function getShiftHandoverSnapshot(recordId: number) {
  return request<IApiResponseData<ShiftHandoverSnapshot>>({
    url: `${PREFIX}merchant_shift_handover_record/snapshot/${recordId}`,
    method: "GET",
  })
}

/** Idempotently open the next shift from the prior confirmed record. */
export function openNextShiftHandover(data: ShiftHandoverOpenNextRequest) {
  return request<IApiResponseData<Worker.HandoverType>>({
    url: `${PREFIX}merchant_shift_handover_record/open_next`,
    method: "POST",
    data,
  })
}

/** 来源：当前后端 OpenAPI 生成的恢复请求。 */
export type ShiftHandoverRecoveryRequest = components["schemas"]["ShiftHandoverRecoveryRequest"]

export function recoverAbnormalShift(data: ShiftHandoverRecoveryRequest) {
  return request<IApiResponseData<Worker.HandoverType>>({
    url: `${PREFIX}merchant_shift_handover_record/abnormal_recovery`,
    method: "POST",
    data,
  })
}

/** 更新交班数据 */
export function updateNow(data: Worker.HandoverType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shift_handover_record`,
    method: "PUT",
    data,
  })
}

/** 修改交接班记录 */
export function update(data: Worker.HandoverType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shift_handover_record/update`,
    method: "POST",
    data,
  })
}

/** 取消交接班 */
export function cancleHandover(merchantShiftHandoverRecordId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shift_handover_record/cancel`,
    method: "POST",
    params: { recordId: merchantShiftHandoverRecordId },
  })
}

/** 打印交班单 */
export function printHandover(
  merchantShiftHandoverRecordId: number,
  time?: HandoverEndTime,
  printerRelId?: number,
) {
  const handoverEndTime = time instanceof Date ? time.toISOString() : time
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_print_info_rel/print-handover`,
    method: "POST",
    params: {
      recordId: merchantShiftHandoverRecordId,
      ...(handoverEndTime ? { handoverEndTime } : {}),
      printerRelId,
    },
  })
}

/** 查询交接班记录 */
export function getRecord(
  merchantUserId: number | null,
  page: number,
  size: number,
  startTime: Date | null,
  endTime: Date | null,
  includeAbnormal = true,
) {
  return request<IApiPageResponseData<Worker.HandoverType>>({
    url: `${PREFIX}merchant_shift_handover_record/record`,
    method: "GET",
    params: {
      merchantUserId,
      includeAbnormal,
      page,
      size,
      startTime: startTime?.toISOString(),
      endTime: endTime?.toISOString(),
    },
  })
}

/** 查询是否存在开班状态 */
export function getOpenStatus() {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shift_handover_record/is_open`,
    method: "GET",
  })
}

/** 获取当前班次信息 */
export function getCurrentShift() {
  return request<IApiResponseData<Worker.HandoverDetailType>>({
    url: `${PREFIX}merchant_shift_handover_record/current_shift`,
    method: "GET",
  })
}

/** 来源：MerchantShiftHandoverRecordController.queryById；只读取班次元数据，不聚合营业报表。 */
export function getHandoverRecordById(recordId: number) {
  return request<IApiResponseData<Worker.MerchantShiftHandoverRecord>>({
    url: `${PREFIX}merchant_shift_handover_record/${recordId}`,
    method: "GET",
  })
}

/** 获取某一班次的营业数据 */
export function getOneCurrentShift(shiftHandoverId: number) {
  return request<IApiResponseData<HandoverBizData>>({
    url: `${PREFIX}merchant_shift_handover_record/biz_data/${shiftHandoverId}`,
    method: "GET",
  })
}

/** 获取某一班次指定 section 的营业数据 */
export function getHandoverSectionBizData(shiftHandoverId: number, section: HandoverBizDataSection) {
  return request<IApiResponseData<HandoverSectionBizDataResponse>>({
    url: `${PREFIX}merchant_shift_handover_record/biz_data/${shiftHandoverId}`,
    method: "GET",
    params: { section },
  })
}

/** 获取某一班次的商品统计 */
export function getHandoverProductStatistics(recordId: number, params: HandoverProductStatisticsRequest) {
  return request<IApiResponseData<HandoverProductStatisticsResponse>>({
    url: `${PREFIX}merchant_shift_handover_record/product_statistics/${recordId}`,
    method: "GET",
    params,
  })
}

/***/
export function getBindStatus() {
  return request<IApiResponseData<Worker.BindStatusType>>({
    url: `${PREFIX}merchant_user/bind-status`,
    method: "GET",
    params: {
      shopId: "-1",
    },
  })
}

/**
 * 新增商户提成表数据
 *
 * @param merchantShopCommission 商户提成表
 * @return 是否新增成功
 */
export function add(merchantShopCommission: Worker.MerchantShopCommission) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_commission`,
    method: "POST",
    data: merchantShopCommission,
  })
}

/**
 * 分页查询商户提成表
 *
 * @param merchantShopCommission 商户提成表
 * @param page 分页对象
 * @return 商户提成表分页数据
 */
export function getCommissionList(merchantShopCommission?: Worker.MerchantShopCommission & IApiPageRequest) {
  return request<IApiPageResponseData<Worker.MerchantShopCommission>>({
    url: `${PREFIX}merchant_shop_commission/page`,
    method: "GET",
    params: merchantShopCommission,
  })
}

/**
 * 通过提成记录表ID查询单条数据
 *
 * @param merchantCommissionRecordId 提成记录表ID
 * @return 提成记录表
 */
export function queryCommissionById(merchantCommissionRecordId: number) {
  return request<IApiResponseData<Worker.MerchantCommissionRecord>>({
    url: `${PREFIX}merchant_commission_record/${merchantCommissionRecordId}`,
    method: "GET",
  })
}

/**
 * 更新商户提成表数据
 *
 * @param merchantShopCommission 商户提成表
 * @return 是否修改成功
 */
export function updateCommission(merchantShopCommission: Worker.MerchantShopCommission) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_commission`,
    method: "PUT",
    data: merchantShopCommission,
  })
}
